import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Calendar } from 'lucide-react';
import { setCanonical, setMeta } from '../utils/seo';
import { BlogSchemas } from './SEOSchemas';
import { SponsorBanner, AdSensePlaceholder, ClickBankBanner } from './AdSlots';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/blogs/${slug}`).then(r => {
      setPost(r.data);
      setCanonical(`/blog/${slug}`);
      setMeta(r.data.title, r.data.excerpt || r.data.content?.slice(0, 160));
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="max-w-3xl mx-auto px-4 py-20 text-center text-[#78716C]">Loading...</div>;
  if (!post) return <div className="max-w-3xl mx-auto px-4 py-20 text-center"><h1 className="text-2xl font-bold mb-4">Post Not Found</h1><Link to="/blog" className="text-[#C5A028]">Back to Blog</Link></div>;

  const renderContent = (content) => {
    return content.split('\n').map((line, i) => {
      if (line.startsWith('## ')) return <h2 key={i} className="font-['Cormorant_Garamond'] text-2xl font-semibold text-[#1C1917] mt-6 mb-3">{line.replace('## ', '')}</h2>;
      if (line.startsWith('### ')) return <h3 key={i} className="font-['Cormorant_Garamond'] text-xl font-semibold text-[#1C1917] mt-4 mb-2">{line.replace('### ', '')}</h3>;
      if (line.startsWith('**') && line.endsWith('**')) return <p key={i} className="font-bold text-[#1C1917] mt-2">{line.replace(/\*\*/g, '')}</p>;
      if (line.trim() === '') return <br key={i} />;
      // Convert markdown links [text](url) to clickable links
      const parts = line.split(/(\[[^\]]+\]\([^)]+\))/g);
      const rendered = parts.map((part, j) => {
        const match = part.match(/\[([^\]]+)\]\(([^)]+)\)/);
        if (match) {
          const isInternal = match[2].startsWith('/') || match[2].includes('truefitguide.com');
          if (isInternal) {
            const href = match[2].replace(/https?:\/\/(www\.)?truefitguide\.com/, '');
            return <Link key={j} to={href} className="text-[#C5A028] hover:underline font-medium">{match[1]}</Link>;
          }
          return <a key={j} href={match[2]} target="_blank" rel="noopener noreferrer" className="text-[#C5A028] hover:underline font-medium">{match[1]}</a>;
        }
        // Auto-link "TrueFitGuide.com" and "TrueFitGuide" mentions
        const textParts = part.split(/(TrueFitGuide\.com|TrueFitGuide)/gi);
        return textParts.map((tp, k) => {
          if (/truefitguide(\.com)?/i.test(tp)) return <Link key={`${j}-${k}`} to="/" className="text-[#C5A028] hover:underline font-medium">{tp}</Link>;
          return tp;
        });
      });
      return <p key={i} className="text-[#78716C] leading-relaxed mb-2">{rendered}</p>;
    });
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-8 py-12">
      <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-[#78716C] hover:text-[#C5A028] mb-8 transition-colors" data-testid="back-to-blog"><ArrowLeft className="w-4 h-4" /> All Articles</Link>
      <article>
        <BlogSchemas post={post} />
        <header className="mb-8">
          <h1 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-light tracking-tighter text-[#1C1917] mb-4" data-testid="blog-title">{post.title}</h1>
          {post.created_at && <p className="text-sm text-[#78716C] flex items-center gap-2"><Calendar className="w-4 h-4" />{new Date(post.created_at).toLocaleDateString()}</p>}
        </header>
        <div className="prose-sm">{renderContent(post.content || '')}</div>
      </article>
      <div className="mt-10 bg-[#FAF8F4] border border-[#E8E2D9] p-6">
        <p className="text-xs text-[#78716C] leading-relaxed"><strong>Disclaimer:</strong> This article is for informational purposes only. Sizes mentioned are approximate. Always verify with the retailer's specific size chart before purchasing.</p>
      </div>
      <div className="mt-6 bg-[#FAF8F4] border border-[#E8E2D9] p-6">
        <h3 className="font-['Cormorant_Garamond'] text-lg font-semibold text-[#1C1917] mb-3">Explore Related Tools</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <Link to="/find-my-size" className="text-sm text-[#C5A028] hover:underline bg-white p-2 border border-[#E8E2D9] text-center">Find My Size</Link>
          <Link to="/compare" className="text-sm text-[#C5A028] hover:underline bg-white p-2 border border-[#E8E2D9] text-center">Compare Brands</Link>
          <Link to="/brands" className="text-sm text-[#C5A028] hover:underline bg-white p-2 border border-[#E8E2D9] text-center">All 150 Brands</Link>
          <Link to="/conversions" className="text-sm text-[#C5A028] hover:underline bg-white p-2 border border-[#E8E2D9] text-center">Size Conversions</Link>
          <Link to="/brand/nike" className="text-sm text-[#C5A028] hover:underline bg-white p-2 border border-[#E8E2D9] text-center">Nike Sizes</Link>
          <Link to="/brand/zara" className="text-sm text-[#C5A028] hover:underline bg-white p-2 border border-[#E8E2D9] text-center">Zara Sizes</Link>
          <Link to="/body-type-guide" className="text-sm text-[#C5A028] hover:underline bg-white p-2 border border-[#E8E2D9] text-center">Body Type Guide</Link>
          <Link to="/convert/us-to-eu" className="text-sm text-[#C5A028] hover:underline bg-white p-2 border border-[#E8E2D9] text-center">US to EU Sizes</Link>
        </div>
      </div>
      <SponsorBanner />
      <ClickBankBanner />
      <AdSensePlaceholder />
    </div>
  );
}
